import styles from "./MileageLogs.module.scss";
import React from "react";
import {
  HeaderMenu,
  HeaderMenuActionDropdown,
  HeaderMenuActionDropdownItem,
  HeaderMenuLeft
} from "../../../common/HeaderMenu";
import {MODE_EDIT} from "../../../codes";
import useMileageLogs from "./useMileageLogs";
import useFormat from "../../../util/useFormat";
import {MileageLogsCars} from "./MileageLogsCars";
import clsx from "clsx";
import {MileageLogsStats} from "./MileageLogsStats";
import {VariableSizeList} from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import {MileageLogEditor} from "../mileage-log-editor/MileageLogEditor";
import useProject from "../../../util/useProject";
import {MileageLogsFab} from "./MileageLogsFab";
import ColoredAmount from "../../../common/ColoredAmount";
import {Content, ContentFixed, ContentScroll} from "../../../common/Content";

const MileageLogs = () => {
  const {formatDate, formatAmount, formatNumber} = useFormat();
  const {runWithExpiration} = useProject();

  const {
    t,
    allCars,
    selectedCar,
    mileageLogs,
    showMileageLogEditor,
    inactiveCars,
    toggleInactiveCars
  } = useMileageLogs();

  const Row = ({index, style}) => {
    const log = mileageLogs[index];

    let conso = <span className={styles.empty}>-</span>;
    let averageConso = <span className={styles.empty}>-</span>;

    if (log.conso) {
      let previousLog = null;

      for (let i = index + 1; i < mileageLogs.length; i++) {
        previousLog = mileageLogs[i];

        if (previousLog.conso) {
          break;
        }
      }

      let diffConso = null;
      let diffAverageConso = null;

      if (previousLog) {
        if (log.conso > previousLog.conso) {
          diffConso = "arrow_drop_up";
        } else if (log.conso < previousLog.conso) {
          diffConso = "arrow_drop_down";
        } else {
          diffConso = "remove";
        }

        if (log.averageConso > previousLog.averageConso) {
          diffAverageConso = "arrow_drop_up";
        } else if (log.averageConso < previousLog.averageConso) {
          diffAverageConso = "arrow_drop_down";
        } else {
          diffAverageConso = "remove";
        }
      }

      conso = <>
        <ColoredAmount amount={log.averageConso - log.conso}>
          {formatNumber(log.conso, "#T##0D000") + " " + selectedCar.consoUnit}
        </ColoredAmount>

        <span className={clsx("material-icons", styles.diffConso)}>{diffConso}</span>
      </>;

      averageConso = <>
        {formatNumber(log.averageConso, "#T##0D000") + " " + selectedCar.consoUnit}

        <span className={clsx("material-icons", styles.diffConso)}>{diffAverageConso}</span>
      </>;
    }

    return <div style={style} className={styles.mileageLog}>
      <div className={clsx(styles.details, "hover-selectable")}
           onClick={runWithExpiration(() => showMileageLogEditor(MODE_EDIT, log))}>
        <div className={styles.carColor}>
          <div className={styles.color} style={{background: selectedCar.color.split("|")[0]}}/>
        </div>
        <div className={styles.carName}>{selectedCar.name}</div>
        <div className={styles.valueDate}>{formatDate(log.valueDate)}</div>
        <div className={styles.odometer}>{formatNumber(log.odometer, "#T##0D##")} {selectedCar.odometerUnit}</div>
        <div className={styles.distance}>
          {log.distance ? formatNumber(log.distance, "#T##0D##") + " " + selectedCar.odometerUnit : "-"}
        </div>
        <div className={styles.quantityPriceTotal}>
          <div className={styles.quantity}>{formatNumber(log.quantity)} {selectedCar.quantityUnit}</div>
          <div className={styles.arobase}>@</div>
          <div className={styles.price}>{formatAmount(log.price)}</div>
          <div className={styles.quantityPriceArrow}><span className="material-icons">arrow_right_alt</span></div>
          <div className={styles.totalPrice}>{formatAmount(log.price * log.quantity)}</div>
        </div>
        <div className={styles.conso}>{conso}</div>
        <div className={clsx(styles.conso, styles.consoAvg)}>{averageConso}</div>
        <div className={styles.consoBorder}/>
      </div>
    </div>;
  };

  return (
    <Content>
      <ContentFixed>
        <HeaderMenu title={t("header_title_mileage_logs")}>
          {allCars.some(c => !c.active) && <HeaderMenuLeft>
            <HeaderMenuActionDropdown label={t("menu_display")}>
              <HeaderMenuActionDropdownItem
                icon={inactiveCars ? "check_box" : "check_box_outline_blank"}
                onClick={toggleInactiveCars}
                enabled
                label={t("menu_show_inactive_cars")}
              />
            </HeaderMenuActionDropdown>
          </HeaderMenuLeft>}
        </HeaderMenu>

        <MileageLogsCars/>

        <MileageLogsStats mileageLogs={mileageLogs} selectedCar={selectedCar}/>

        <MileageLogEditor/>

        <MileageLogsFab/>

        <div className={clsx(styles.mileageLog, styles.header)}>
          <div className={styles.details}>
            <div className={styles.carColor}/>
            <div className={styles.carName}>{t("list_header_car")}</div>
            <div className={styles.valueDate}>{t("list_header_date")}</div>
            <div className={styles.odometer}>{t("list_header_odometer")}</div>
            <div className={styles.distance}>{t("list_header_distance")}</div>
            <div className={styles.quantity}>{t("list_header_quantity")}</div>
            <div className={styles.price}>{t("list_header_price")}</div>
            <div className={styles.totalPrice}>{t("list_header_total_price")}</div>
            <div className={styles.conso}>{t("list_header_conso")}</div>
            <div className={styles.conso}>{t("list_header_average_conso")}</div>
          </div>
        </div>
      </ContentFixed>

      <ContentScroll whiteBackground>
        <AutoSizer>
          {({height, width}) => (
            <VariableSizeList
              className="custom-scrollbar autosizer-scrollbar"
              style={{overflowY: "scroll"}}
              height={height - 0.5}
              itemCount={mileageLogs.length}
              itemSize={() => width < 880 ? 75 : 30}
              estimatedItemSize={width < 880 ? 75 : 30}
              itemKey={index => mileageLogs[index].id}
              width={width}
              overscanCount={10}
              useIsScrolling
            >
              {Row}
            </VariableSizeList>
          )}
        </AutoSizer>
      </ContentScroll>
    </Content>
  );
};

export default MileageLogs;
